import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

// The one page header.
//
// There used to be five: a custom div with an ::after rule and an italic
// date (Dashboard), a white bar holding the account switcher and a logout
// button (Settings), a bare h5 (Users, Companies), an h2 with a subtitle
// (Files), and nothing at all (Operations). Which meant the title moved
// vertically, changed size, and changed weight every time you clicked a
// different tab.
//
// Breadcrumbs and the account switcher are NOT here — they belong to the
// topbar, which is always in the same place. This is only the title, the
// one-line explanation of what the screen is for, and the screen's actions.

interface Props {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /** Sits under the header, above the divider — filters, tabs, a search box. */
  children?: React.ReactNode;
  divider?: boolean;
}

const PageHeader: React.FC<Props> = ({ title, subtitle, actions, children, divider = true }) => (
  <Box
    sx={{
      mb: 3,
      pb: children ? 0 : 2,
      borderBottom: divider && !children ? '1px solid' : 0,
      borderColor: 'divider',
    }}
  >
    {/* Wraps rather than squeezes. The actions used to refuse to shrink
        and the title was allowed to, so between `sm` and a wide window
        the title collapsed to a few letters with the buttons drawn on
        top of it. Now the title keeps at least 320px and the actions
        drop to their own line when the two no longer fit side by side. */}
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={2}
    >
      <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
        <Typography variant="h2" sx={{ lineHeight: 1.2 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ minWidth: 0, alignItems: 'center' }}>
          {actions}
        </Stack>
      )}
    </Stack>

    {children && (
      <Box
        sx={{
          mt: 2.5,
          pb: 2,
          borderBottom: divider ? '1px solid' : 0,
          borderColor: 'divider',
        }}
      >
        {children}
      </Box>
    )}
  </Box>
);

export default PageHeader;
